import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm]   = useState({ email: '', password: '', companyName: '', address: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPass, setShowPass] = useState(false);

    // Auto-fill company name from email domain when user tabs out
    const handleEmailBlur = () => {
        if (!form.companyName && form.email.includes('@')) {
            const domain = form.email.split('@')[1]?.split('.')[0] || '';
            if (domain) setForm(f => ({ ...f, companyName: domain.charAt(0).toUpperCase() + domain.slice(1) }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
        setError(''); setLoading(true);
        try {
            await register(form.email, form.password, form.companyName, form.address);
            navigate('/sales/invoices');
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed. Please try again.');
        } finally { setLoading(false); }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                * { box-sizing:border-box; margin:0; padding:0; }
                body { font-family:'Inter',sans-serif; }
                .auth-input { width:100%; padding:13px 16px; border-radius:10px; border:1.5px solid #e2e8f0; font-size:15px; font-family:'Inter',sans-serif; color:#1e293b; background:#f8fafc; outline:none; transition:all 0.2s ease; }
                .auth-input:focus { border-color:#10b981; background:#fff; box-shadow:0 0 0 4px rgba(16,185,129,0.1); }
                .auth-btn { width:100%; padding:15px; border:none; border-radius:12px; font-size:16px; font-weight:700; font-family:'Inter',sans-serif; cursor:pointer; transition:all 0.25s cubic-bezier(0.4,0,0.2,1); display:flex; align-items:center; justify-content:center; gap:8px; }
                .auth-btn:hover { transform:translateY(-2px); }
                .auth-btn:active { transform:scale(0.98); }
                @keyframes fadeSlideUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
                @keyframes floatBg { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-20px)} }
                .auth-card { animation:fadeSlideUp 0.5s cubic-bezier(0.4,0,0.2,1); }
                .blob1 { animation:floatBg 6s ease-in-out infinite; }
                .blob2 { animation:floatBg 8s ease-in-out infinite reverse; }
            `}</style>

            <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#064e3b 0%,#065f46 50%,#064e3b 100%)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', position:'relative', overflow:'hidden' }}>

                <div className="blob1" style={{ position:'absolute', width:'400px', height:'400px', borderRadius:'50%', background:'radial-gradient(circle,rgba(16,185,129,0.15),transparent 70%)', top:'-100px', left:'-100px', pointerEvents:'none' }} />
                <div className="blob2" style={{ position:'absolute', width:'350px', height:'350px', borderRadius:'50%', background:'radial-gradient(circle,rgba(5,150,105,0.12),transparent 70%)', bottom:'-80px', right:'-80px', pointerEvents:'none' }} />

                <div className="auth-card" style={{ width:'100%', maxWidth:'460px', margin:'20px', background:'rgba(255,255,255,0.98)', borderRadius:'24px', padding:'44px', boxShadow:'0 30px 80px rgba(0,0,0,0.4)' }}>

                    {/* Brand */}
                    <div style={{ textAlign:'center', marginBottom:'32px' }}>
                        <div style={{ width:'56px', height:'56px', background:'linear-gradient(135deg,#10b981,#059669)', borderRadius:'16px', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'28px', marginBottom:'16px', boxShadow:'0 8px 20px rgba(16,185,129,0.4)' }}>🛡️</div>
                        <h1 style={{ fontSize:'26px', fontWeight:'800', color:'#0f172a', letterSpacing:'-0.5px' }}>Create your account</h1>
                        <p style={{ fontSize:'14px', color:'#94a3b8', marginTop:'6px' }}>Start managing invoices professionally</p>
                    </div>

                    {/* Company name auto-fill notice */}
                    <div style={{ background:'#f0fdf4', border:'1px solid #a7f3d0', color:'#065f46', padding:'11px 14px', borderRadius:'10px', fontSize:'13px', marginBottom:'20px', display:'flex', alignItems:'center', gap:'8px' }}>
                        💡 Your <strong>company name</strong> will be auto-filled from your email domain — you can edit it below.
                    </div>

                    {error && (
                        <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', color:'#dc2626', padding:'12px 16px', borderRadius:'10px', fontSize:'14px', marginBottom:'18px', display:'flex', alignItems:'center', gap:'8px' }}>
                            ⚠️ {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Email */}
                        <div style={{ marginBottom:'14px' }}>
                            <label style={{ display:'block', fontSize:'13px', fontWeight:'600', color:'#475569', marginBottom:'6px' }}>📧 Work Email *</label>
                            <input className="auth-input" type="email" placeholder="you@yourcompany.com" required
                                value={form.email}
                                onChange={e => setForm({...form, email: e.target.value})}
                                onBlur={handleEmailBlur} />
                        </div>

                        {/* Company Name */}
                        <div style={{ marginBottom:'14px' }}>
                            <label style={{ display:'block', fontSize:'13px', fontWeight:'600', color:'#475569', marginBottom:'6px' }}>🏢 Company Name <span style={{ color:'#94a3b8', fontWeight:'400' }}>(auto-filled from email)</span></label>
                            <input className="auth-input" type="text" placeholder="e.g. Codevirus Security" required
                                value={form.companyName} onChange={e => setForm({...form, companyName: e.target.value})} />
                        </div>

                        {/* Address */}
                        <div style={{ marginBottom:'14px' }}>
                            <label style={{ display:'block', fontSize:'13px', fontWeight:'600', color:'#475569', marginBottom:'6px' }}>📍 Business Address <span style={{ color:'#94a3b8', fontWeight:'400' }}>(optional)</span></label>
                            <textarea className="auth-input" placeholder="Street, City, State, PIN" rows={2} style={{ resize:'none' }}
                                value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
                        </div>

                        {/* Password */}
                        <div style={{ marginBottom:'26px' }}>
                            <label style={{ display:'block', fontSize:'13px', fontWeight:'600', color:'#475569', marginBottom:'6px' }}>🔒 Password *</label>
                            <div style={{ position:'relative' }}>
                                <input className="auth-input" type={showPass ? 'text' : 'password'} placeholder="Min. 6 characters" required
                                    value={form.password} onChange={e => setForm({...form, password: e.target.value})} style={{ paddingRight:'50px' }} />
                                <button type="button" onClick={() => setShowPass(!showPass)}
                                    style={{ position:'absolute', right:'14px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:'18px', color:'#94a3b8' }}>
                                    {showPass ? '🙈' : '👁️'}
                                </button>
                            </div>
                            {/* Password strength bar */}
                            <div style={{ marginTop:'8px', display:'flex', gap:'4px' }}>
                                {[1,2,3,4].map(n => (
                                    <div key={n} style={{ flex:1, height:'3px', borderRadius:'2px', background: form.password.length >= n*2 ? (form.password.length >= 8 ? '#10b981' : '#f59e0b') : '#e2e8f0', transition:'background 0.3s' }} />
                                ))}
                            </div>
                        </div>

                        <button className="auth-btn" type="submit" disabled={loading}
                            style={{ background: loading ? '#94a3b8' : 'linear-gradient(135deg,#10b981,#059669)', color:'white', boxShadow: loading ? 'none' : '0 6px 20px rgba(16,185,129,0.4)', marginBottom:'16px' }}>
                            {loading ? '⏳ Creating Account...' : '✅ Create Account & Start'}
                        </button>
                    </form>

                    <div style={{ textAlign:'center', fontSize:'14px', color:'#64748b', borderTop:'1px solid #f1f5f9', paddingTop:'20px', marginTop:'8px' }}>
                        Already have an account?{' '}
                        <Link to="/login" style={{ color:'#10b981', fontWeight:'700', textDecoration:'none' }}>Sign In →</Link>
                    </div>
                </div>
            </div>
        </>
    );
}
